import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { AuthStore } from '../../core/store/auth-store';
import { AuthRoles } from '@app/core/models/auth-roles';
import { withStatusMessage } from '@app/core/services/status-message-interceptor';
import { SKIP_LOADER } from '@app/core/services/loading-interceptor';
import { CompanyStatus } from '../models/company-status';
import { Router } from '@angular/router';
import { CompanyApi } from '../services/company-api';
import { Company } from '../models/company';
import { MaintenanceMessage } from '../models/maintenance-message';
import { MaintenanceMessageApi } from '../services/maintenance-message-api';
import { MessageService } from '@openng/optimus-ui/api';
import { TranslocoService } from '@jsverse/transloco';

interface AppState {
  companies: Company[];
  isCompanyStatusDoneLoading: boolean;
  accessibleRoutes: string[];
  isAccessibleRoutesDoneLoading: boolean;
  companyId: number | null;
  maintenanceMessage: MaintenanceMessage | null;
}

const initialState: AppState = {
  companies: [],
  isCompanyStatusDoneLoading: false,
  accessibleRoutes: [],
  isAccessibleRoutesDoneLoading: false,
  companyId: null,
  maintenanceMessage: null,
};

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _maintenanceMessageApi: inject(MaintenanceMessageApi),
    _companyApi: inject(CompanyApi),
    _authStore: inject(AuthStore),
    _router: inject(Router),
    _messageService: inject(MessageService),
    _translationService: inject(TranslocoService),
  })),
  withComputed(({ companies, companyId }) => ({
    company: computed(
      () => companies().find(x => x.id === companyId()) ?? null
    ),
  })),
  withMethods(store => ({
    isFirstAidRoute: () => store._router.url.startsWith('/firstaid/'),
    isVisionRoute: () => store._router.url.startsWith('/vision/'),
    isRouteAccessible(route?: string) {
      const normalizeRoute = (route: string): string => {
        route = (route as string).replace(/^\/+/, '').replace(/\/+$/, '');
        const path = route.split('?')[0].replace(/^\/+|\/+$/g, '');
        const isId = (s: string) =>
          /^\d+$/.test(s) ||
          /^\d+(?:-\d+)+$/.test(s) ||
          s.toLowerCase() === 'new';
        return path
          .split('/')
          .map(seg => (isId(seg) ? ':id' : seg))
          .join('/');
      };
      if (route) {
        return store.accessibleRoutes().includes(normalizeRoute(route));
      }
      return true;
    },
    loadCompanies() {
      patchState(store, { isCompanyStatusDoneLoading: false });
      store._companyApi
        .getOwn(
          undefined,
          withStatusMessage(true, false).set(SKIP_LOADER, true)
        )
        .subscribe({
          next: companies => {
            patchState(store, {
              companies: companies,
              isCompanyStatusDoneLoading: true,
            });
          },
          error: () => {
            patchState(store, {
              companies: [],
              isCompanyStatusDoneLoading: true,
            });
          },
        });
    },
    updateCompanyId(id?: number) {
      this._updateCompanyId(id, true);
      store._messageService.add({
        severity: 'success',
        summary: store._translationService.getTranslation('message')['success'],
      });
    },
    _updateCompanyId(id?: number | null, reload?: boolean) {
      const sortedCompanies = [...store.companies()].sort((a, b) => {
        if (a.active === b.active) return 0;
        return a.active ? -1 : 1;
      });
      const defaultCompanyId = sortedCompanies[0]?.id;

      const previousId = store.companyId();

      let nextId: number | null;

      if (id && store.companies().find(x => x.id === id)) {
        nextId = id as number;
      } else if (defaultCompanyId) {
        nextId = defaultCompanyId as number;
      } else {
        nextId = null;
      }

      if (nextId !== previousId) {
        patchState(store, { companyId: nextId });
        if (typeof window !== 'undefined' && reload) {
          store._router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              store._router.navigateByUrl('/auth-callback');
            });
        }
      }
    },
    _updateAccessibleRoutes() {
      patchState(store, {
        isAccessibleRoutesDoneLoading: false,
      });

      if (
        !store._authStore.isDoneLoading() ||
        !store.isCompanyStatusDoneLoading()
      ) {
        return;
      }

      const roles = store._authStore.roles();
      const company = store.company();
      const accessibleRoutes: string[] = [];

      const routes = (): string[] => {
        const all: string[] = [];

        const walk = (routes: typeof store._router.config, base = ''): void => {
          for (const route of routes) {
            if (route.redirectTo) continue;

            const segment = (route.path ?? '').trim();
            const full = joinPath(base, segment);

            // only add if route has a concrete component target
            const hasComponent =
              !!(route as { component?: unknown }).component ||
              !!(route as { loadComponent?: unknown }).loadComponent;

            if (segment && hasComponent) {
              all.push(full);
            }

            if (route.children?.length) {
              walk(route.children, full);
            }
          }
        };

        const joinPath = (parent: string, child: string): string => {
          const p = parent.replace(/\/+$/, '');
          const c = child.replace(/^\/+/, '');
          if (!p && !c) return '';
          if (!p) return c;
          if (!c) return p;
          return `${p}/${c}`;
        };

        walk(store._router.config);

        return all;
      };

      routes().forEach(route => {
        switch (route) {
          case 'validityCheck':
            if (!store._authStore.isAuthenticated()) {
              accessibleRoutes.push(route);
            }
            return;
          case 'late-submission':
            if (!store._authStore.isAuthenticated()) {
              accessibleRoutes.push(route);
            }
            return;
          case 'signup':
            if (!(roles?.length > 0)) {
              accessibleRoutes.push(route);
            }
            break;
          case 'no-access':
            if (store._authStore.isAuthenticated() && !(roles?.length > 0)) {
              accessibleRoutes.push(route);
            }
            break;
          case 'profile':
          case 'dashboard':
          case 'auth-callback':
            if (roles?.length > 0) {
              accessibleRoutes.push(route);
            }
            break;
          case 'admin/auditlog':
          case 'admin/status':
          case 'admin/api-token':
          case 'admin/api-token/:id':
          case 'admin/billing-event/transmissions':
          case 'admin/liechtenstein-certificate':
            if (roles?.includes(AuthRoles.ASA_Admin)) {
              accessibleRoutes.push(route);
            }
            break;
          case 'admin/billing-event':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              ((roles?.includes(AuthRoles.NHK_Admin) ||
                roles?.includes(AuthRoles.Vision_Admin)) &&
                company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/application':
          case 'firstaid/application/:id':
          case 'firstaid/courseprovider':
          case 'firstaid/disposition/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              roles?.includes(AuthRoles.ASTRA_Admin)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/registration':
            if (
              roles?.includes(AuthRoles.NHK_Admin) &&
              (company === null ||
                company?.status === CompanyStatus.UnderReview ||
                company?.status === CompanyStatus.Declined ||
                company?.status === CompanyStatus.Revoked)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/recertification':
            if (
              roles?.includes(AuthRoles.NHK_Admin) &&
              (company?.status === CompanyStatus.QssPossible ||
                company?.status === CompanyStatus.QssExpired ||
                company?.status === CompanyStatus.QssExceeded)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/person':
          case 'firstaid/person/:id':
          case 'firstaid/coursetype':
          case 'firstaid/coursetype/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              (roles?.includes(AuthRoles.NHK_Admin) && company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/coursegroup':
          case 'firstaid/coursegroup/:id':
          case 'firstaid/participant':
          case 'firstaid/participant/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              ((roles?.includes(AuthRoles.NHK_Admin) ||
                roles?.includes(AuthRoles.NHK_User)) &&
                company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/courseaddress':
          case 'firstaid/courseaddress/:id':
            if (roles?.includes(AuthRoles.NHK_Admin) && company?.active) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/late-submission':
          case 'firstaid/late-submission/:id':
          case 'vision/late-submission':
          case 'vision/late-submission/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              roles?.includes(AuthRoles.STVA_Admin)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'vision/registration':
            if (roles?.includes(AuthRoles.Vision_Admin) && company === null) {
              accessibleRoutes.push(route);
            }
            break;
          case 'vision/person':
          case 'vision/person/:id':
          case 'vision/optician':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              (roles?.includes(AuthRoles.Vision_Admin) && company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'vision/optician/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              ((roles?.includes(AuthRoles.Vision_Admin) ||
                roles?.includes(AuthRoles.Vision_User)) &&
                company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'firstaid/courseprovider/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              roles?.includes(AuthRoles.ASTRA_Admin) ||
              ((roles?.includes(AuthRoles.NHK_Admin) ||
                roles?.includes(AuthRoles.NHK_User)) &&
                company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          case 'vision/test-result':
          case 'vision/test-result/:id':
            if (
              roles?.includes(AuthRoles.ASA_Admin) ||
              roles?.includes(AuthRoles.STVA_Admin) ||
              ((roles?.includes(AuthRoles.Vision_Admin) ||
                roles?.includes(AuthRoles.Vision_User)) &&
                company?.active)
            ) {
              accessibleRoutes.push(route);
            }
            break;
          default:
            break;
        }
      });

      patchState(store, {
        accessibleRoutes: accessibleRoutes,
        isAccessibleRoutesDoneLoading: true,
      });
    },
  })),
  withHooks(store => ({
    onInit: () => {
      store._maintenanceMessageApi.get().subscribe({
        next: maintenanceMessage => {
          patchState(store, { maintenanceMessage });
        },
        error: () => {
          patchState(store, { maintenanceMessage: null });
        },
      });

      effect(() => {
        const roles = store._authStore.roles();
        if (
          store._authStore.isDoneLoading() &&
          store._authStore.isAuthenticated() &&
          (roles.includes(AuthRoles.NHK_Admin) ||
            roles.includes(AuthRoles.NHK_User) ||
            roles.includes(AuthRoles.Vision_Admin) ||
            roles.includes(AuthRoles.Vision_User))
        ) {
          store.loadCompanies();
        } else {
          patchState(store, {
            companies: [],
            isCompanyStatusDoneLoading: true,
          });
        }
      });

      effect(() => {
        store._authStore.roles();
        if (
          store._authStore.isDoneLoading() &&
          store.isCompanyStatusDoneLoading()
        ) {
          store._updateCompanyId(store.companyId());
        }
      });

      effect(() => {
        store.companyId();
        store._authStore.isDoneLoading();
        store.isCompanyStatusDoneLoading();
        store._updateAccessibleRoutes();
      });
    },
  }))
);
